import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = '/api';

// Configure axios to always send the token
function setAxiosToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('access_token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('access_token');
  }
}

// Restore token on load
const savedToken = localStorage.getItem('access_token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Helper to format API errors
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
      setAxiosToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      if (data.access_token) {
        setAxiosToken(data.access_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setAxiosToken(null);
      setUser(false);
    }
  };

  const changePassword = async (newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
      // Refresh user data to clear the must_reset_password flag
      await checkAuth();
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
